import React, { Dispatch, SetStateAction } from 'react';
import Modal from '../../components/modal/Modal';
import { Col, convertToKRW, Row } from '../../lib/utils';
import { BoldText, DemiLightText, MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import SvgIcon from '../../components/SvgIcon';
import { MembershipInfo } from '../../services/useMemberQueryService';
import dayjs from 'dayjs';
import { useMembershipTheme } from '../../components/MemberProfile/useMembershipTheme';
import SettlementBasisRow, { useSettlementBasis } from './components/settlementBasis/SettlementBasisRow';

type Props = {
    setIsVisible: Dispatch<SetStateAction<boolean>>;
    membershipInfo: MembershipInfo;
    /** false 면 회원 나이·성별 줄을 숨긴다 — 그 값을 받지 못한 진입점(세션 현황)에서 「미입력」으로 잘못 보이지 않게. */
    showMemberDetails?: boolean;
};

const ModalMembershipInfo = ({ setIsVisible, membershipInfo, showMemberDetails = true }: Props) => {
    // 강사이름, 회원 이름
    const coachName = membershipInfo?.coachName;
    const memberName = membershipInfo?.memberInfo?.memberName;
    const memberAge = membershipInfo?.memberInfo?.age;
    const isNewSalesText = membershipInfo.memberInfo.isNewSales ? '신규' : '재등록';
    const memberGender = membershipInfo?.memberInfo?.gender
        ? membershipInfo?.memberInfo?.gender === 'male'
            ? '남성'
            : '여성'
        : '미입력';
    // 현재 index

    // const membership = membershipInfo?.memberships
    const [membership, setMembership] = React.useState(membershipInfo.membership);
    // 다시 받은 수강권(세션 현황에서 정정 뒤 재조회 등)이 오면 데이터만 갈아 끼운다. 창을 새로 그리지 않으므로
    // 안에 열린 정산 기준 수정 창의 입력·오류·완료 안내는 그대로 남는다(4회차 지적 3).
    const incomingMembership = membershipInfo.membership;
    const isFirstMembershipRef = React.useRef(true);
    React.useEffect(() => {
        if (isFirstMembershipRef.current) {
            isFirstMembershipRef.current = false;
            return;
        }
        setMembership(incomingMembership);
    }, [incomingMembership]);
    // 수강권 테마 — gradient/배지/정책 라벨 일원화 (Phase 2 UX 통일화)
    const theme = useMembershipTheme(membership);
    const { isGroup, headerBackground, expiryPolicyLabel, sourceLabel } = theme;
    const membershipName = membership?.name;
    const startDate = dayjs(membership?.activationDate).format('YYYY-MM-DD');
    const endDate = dayjs(membership?.expirationDate).format('YYYY-MM-DD');
    // 현재회차, 총회차, 회당 가격, 발급일자
    const currentSession = membership.currentSession;
    const totalSession = membership.totalSession;
    const pricePerSchedule = membership.pricePerSchedule;
    const createdAt = dayjs(membership.createdAt).format('YYYY-MM-DD');
    // 정산 기준 — 센터가 정산 분류를 쓰거나 정정된 수강권일 때만 카드 안 한 줄로(안 쓰는 센터는 바꾸기 전 그대로).
    const settlementBasis = useSettlementBasis(membership);

    return (
        <Modal
            whiteViewWidth={587}
            setIsVisible={setIsVisible}
            isCloseOutside={false}
            borderRadius={37}
            modalAlign="center"
            isCloseButton
        >
            <Col className="flex px-[35px] pt-[35px] items-center" style={{ width: '100%' }}>
                {/* 타이틀 + 배지 (line 1) */}
                <Row className="items-center mb-[14px] self-start" style={{ gap: 12, flexWrap: 'wrap' }}>
                    <MediumText fontSize={28} lineHeight={38} color="#3D4244">
                        수강권 정보
                    </MediumText>
                    <span
                        style={{
                            fontSize: 13,
                            fontWeight: 700,
                            padding: '4px 10px',
                            borderRadius: 6,
                            backgroundColor: isGroup ? '#DCFCE7' : '#DBEAFE',
                            color: isGroup ? '#166534' : '#1E40AF',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {sourceLabel}
                    </span>
                    {expiryPolicyLabel && (
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 600,
                                padding: '4px 10px',
                                borderRadius: 6,
                                backgroundColor: '#F3F4F6',
                                color: '#374151',
                                whiteSpace: 'nowrap',
                            }}
                            title={`만료 정책: ${expiryPolicyLabel}`}
                        >
                            {expiryPolicyLabel}
                        </span>
                    )}
                </Row>

                {/* 강사 → 회원 (line 2) */}
                <Row className="items-center mb-[22px] self-start" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <MediumText fontSize={18} lineHeight={26} color="#3D4244">
                        {coachName} 강사
                    </MediumText>
                    <SvgIcon
                        name={'SvgRightStrokeArrow'}
                        size={18}
                        fill={colors.trans}
                        stroke="#3D4244"
                        strokeWidth={1.5}
                    />
                    <MediumText fontSize={18} lineHeight={26} color="#3D4244">
                        {memberName} 회원
                    </MediumText>
                </Row>
                <Col className="flex relative py-[22px] px-[24px] rounded-[16px] border border-solid border-[#E5E5E9] w-[345px] h-[102px] bg-[#F2F4F6] mb-[17px]">
                    <Row>
                        <SvgIcon name={'SvgProfileInWhiteCircle'} size={58} fill={colors.trans} className="mr-[12px]" />
                        <Col>
                            <MediumText fontSize={23} lineHeight={33} color="#3D4244">
                                {memberName}
                            </MediumText>
                            {showMemberDetails && (
                            <Row className="items-center">
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {memberAge ? memberAge + '세' : '미입력'}
                                </RegularText>
                                <div className="h-[14px] bg-black w-[1px] mx-[11px]" />
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    {memberGender}
                                </RegularText>
                            </Row>
                            )}
                        </Col>
                    </Row>
                    <Col className=" absolute right-[28px] bg-[#7E7EF5] rounded-[16.5px] w-[54px] h-[33px] items-center justify-center">
                        <MediumText fontSize={14} lineHeight={20} color="white">
                            {isNewSalesText}
                        </MediumText>
                    </Col>
                </Col>
                <Row className=" items-center mb-[35px]">
                    <Col className="w-[345px]">
                        <Col>
                            <Col
                                className="flex flex-col w-full rounded-tr-[12px] rounded-tl-[12px] h-[123px] justify-center items-center "
                                style={{
                                    background: headerBackground,
                                    alignItems: 'center',
                                }}
                            >
                                <Col className="flex w-full p-[32px] justify-start items-start">
                                    <BoldText fontSize={18} lineHeight={28} color="white" className="w-full h-[56px]">
                                        {membershipName}
                                    </BoldText>
                                    <Row className=" items-center">
                                        <Col className="bg-white bg-opacity-[34%] rounded-[18px] w-[60px] h-[25px] justify-center items-center mr-[7px] ">
                                            <RegularText fontSize={13} lineHeight={18} color="white">
                                                이용 기간
                                            </RegularText>
                                        </Col>
                                        <RegularText
                                            fontSize={15}
                                            lineHeight={21.72}
                                            color="white"
                                            className=" opacity-[0.6]"
                                        >
                                            {`${startDate} ~ ${endDate}`}
                                        </RegularText>
                                    </Row>
                                </Col>
                            </Col>
                            <Col
                                className={`w-full ${
                                    settlementBasis.visible ? 'min-h-[222px]' : 'h-[183px]'
                                } border border-solid border-[#D9D9D9] rounded-bl-[21px] rounded-br-[21px] px-[24px] justify-between py-[20px]`}
                            >
                                <Row className="w-full justify-between">
                                    <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                        현재 회차
                                    </DemiLightText>
                                    <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                                        {currentSession + '회'}
                                    </MediumText>
                                </Row>
                                <Row className="w-full justify-between">
                                    <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                        총 회차
                                    </DemiLightText>
                                    <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                                        {totalSession + '회'}
                                    </MediumText>
                                </Row>
                                <Row className="w-full justify-between">
                                    <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                        회당 가격
                                    </DemiLightText>
                                    <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                                        {convertToKRW(pricePerSchedule ?? 0) + '원'}
                                    </MediumText>
                                </Row>
                                <Row className="w-full justify-between">
                                    <DemiLightText fontSize={16} lineHeight={23} color="#505967">
                                        발급 일자
                                    </DemiLightText>
                                    <MediumText fontSize={16} lineHeight={23} color="#3D4244">
                                        {createdAt}
                                    </MediumText>
                                </Row>
                                {settlementBasis.visible && (
                                    <SettlementBasisRow
                                        membership={membership}
                                        isNewSales={membershipInfo?.memberInfo?.isNewSales}
                                        centerId={settlementBasis.centerId}
                                        categories={settlementBasis.categories}
                                        settlementType={settlementBasis.settlementType}
                                        onCorrected={(patch) => setMembership((prev: any) => ({ ...prev, ...patch }))}
                                    />
                                )}
                            </Col>
                        </Col>
                    </Col>
                </Row>
            </Col>
        </Modal>
    );
};

export default ModalMembershipInfo;
